import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} ImageUpload
 * @property {string|null} url
 * @property {Object} small
 * @property {string|null} small.url
 * @property {Object} medium
 * @property {string|null} medium.url
 * @property {Object} initial
 * @property {string|null} initial.url
 */

/**
 * @typedef {Object} DocumentUpload
 * @property {string|null} url
 */

/**
 * @typedef {Object} Location
 * @property {number} id
 * @property {string} name
 * @property {number} portal_id
 * @property {string|null} description
 * @property {string|null} external_id
 * @property {string|null} external_updated_at
 * @property {string|null} discarded_at
 * @property {string} created_at
 * @property {string} updated_at
 * @property {number} order
 * @property {boolean} is_active
 * @property {boolean} is_default
 * @property {string} location_code
 * @property {string|null} order_count
 * @property {boolean} coop_enable
 * @property {boolean} coop_overwrite
 * @property {number} coop_percentage_100
 * @property {boolean} show_price
 * @property {boolean} hide_header_menu
 * @property {string|null} checkout_information_html
 * @property {boolean} show_checkout_information
 * @property {boolean} show_customer_note_input
 * @property {boolean} customer_note_input_required
 * @property {boolean} requires_approval
 * @property {string} default_fulfillment_status
 * @property {boolean} hide_products_out_of_stock
 * @property {string|null} conversion_ratio
 * @property {string|null} sort_order
 * @property {string|null} billing_poc_payments
 * @property {string|null} billing_poc_emails
 * @property {string|null} location_poc_emails
 * @property {string} custom_invoice_message
 * @property {boolean} show_stock_count
 * @property {boolean} all_products_require_approval
 * @property {number} custom_checkout_fee
 * @property {string|null} custom_checkout_fee_alias
 * @property {boolean} disable_client_side_invoicing
 * @property {string|null} checkout_form_id
 * @property {number} location_group_id
 * @property {string} status
 * @property {string|null} new_location_group_id
 * @property {boolean} require_billing_address_for_free_orders
 * @property {number} agency_id
 * @property {number} punchout
 */

/**
 * @typedef {Object} Page
 * @property {number} id
 * @property {string} title
 * @property {string|null} meta_title
 * @property {string|null} meta_desc
 * @property {number} portal_id
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} active_url
 * @property {string|null} active_component
 * @property {string|null} generic_template_id
 * @property {string|null} home_template_id
 * @property {string|null} about_template_id
 * @property {string|null} meta_author
 * @property {string|null} meta_url
 * @property {ImageUpload} meta_image
 * @property {string|null} meta_image_alt
 * @property {string|null} order
 * @property {boolean} visible
 * @property {string|null} discarded_at
 * @property {string} page_type
 * @property {string|null} location_id
 * @property {number} agency_id
 */

/**
 * @typedef {Object} ContextDictionary
 * @property {number} id
 * @property {number} client_id
 * @property {string} coop_def
 * @property {string} location_def
 * @property {string} ecpid_def
 * @property {string} eclid_def
 * @property {string} ecaid_def
 * @property {string} shop_def
 * @property {string} expiry_def
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} coop_abbreviation
 * @property {string|null} account_credit_def
 * @property {string|null} net_term_def
 * @property {string|null} net_term_po_number
 * @property {number} agency_id
 */

/**
 * @typedef {Object} CustomerGroup
 * @property {number} id
 * @property {string} name
 * @property {number} portal_id
 * @property {string} created_at
 * @property {string} updated_at
 * @property {number} agency_id
 */

/**
 * @typedef {Object} NoUserCustomerGroup
 * @property {number} id
 * @property {string} customer_group_name
 * @property {boolean} net_30_payments
 * @property {boolean} hide_products
 * @property {boolean} hide_credit_card
 * @property {boolean} disable_hybrid_shipping
 * @property {Array} hidden_products_list
 * @property {Array} hidden_categories_list
 * @property {Array} hidden_artworks_list
 */

/**
 * @typedef {Object} PortalIntegration
 * @property {number} id
 * @property {number} portal_id
 * @property {number} agency_id
 * @property {string} provider_type
 * @property {string} display_name
 * @property {string} connection_name
 * @property {string} name
 * @property {boolean} is_enabled
 */

/**
 * @typedef {Object} CcDataPointOption
 * @property {number} id
 * @property {string} value
 * @property {string} label
 * @property {string} entity_type
 * @property {number} entity_id
 * @property {number} agency_id
 */

/**
 * @typedef {Object} CcDataPoint
 * @property {number} id
 * @property {string} name
 * @property {string} alias
 * @property {CcDataPointOption[]} cc_data_point_options
 */

/**
 * @typedef {Object} MenuItem
 * @property {number} id
 * @property {string} name
 * @property {string} url
 * @property {number} order
 * @property {number} portal_menu_id
 * @property {string} created_at
 * @property {string} updated_at
 * @property {number} agency_id
 */

/**
 * @typedef {Object} PortalMenu
 * @property {number} id
 * @property {number} portal_id
 * @property {string|null} theme
 * @property {MenuItem[]} menu_items
 */

/**
 * @typedef {Object} Portal
 * @property {number} id
 * @property {number} client_id
 * @property {string} client_name
 * @property {string} name
 * @property {string|null} description
 * @property {string} created_at
 * @property {string} updated_at
 * @property {number} client_api_token_id
 * @property {ImageUpload} logo
 * @property {ImageUpload} secondary_image
 * @property {ImageUpload} favicon
 * @property {string|null} meta_locale
 * @property {string|null} meta_type
 * @property {string|null} meta_title
 * @property {string|null} meta_site_name
 * @property {string|null} meta_twitter_card
 * @property {ImageUpload} meta_image
 * @property {string|null} meta_description
 * @property {string|null} meta_image_alt
 * @property {string|null} custom_css_block
 * @property {string} stylesheet
 * @property {string|null} footer_text_1
 * @property {string|null} footer_text_2
 * @property {string|null} footer_link_1
 * @property {string|null} footer_link_1_text
 * @property {string|null} footer_link_2
 * @property {string|null} footer_link_2_text
 * @property {string|null} footer_link_3
 * @property {string|null} footer_link_3_text
 * @property {string|null} footer_link_4
 * @property {string|null} footer_link_4_text
 * @property {string|null} footer_link_5
 * @property {string|null} footer_link_5_text
 * @property {string|null} footer_link_6
 * @property {string|null} footer_link_6_text
 * @property {string} footer_open_mon
 * @property {string} footer_open_tues
 * @property {string} footer_open_wed
 * @property {string} footer_open_thurs
 * @property {string} footer_open_fri
 * @property {string} footer_open_sat
 * @property {boolean|null} portal_has_eshop
 * @property {string|null} discarded_at
 * @property {string} code
 * @property {boolean} coop_enabled
 * @property {boolean|null} requires_approval
 * @property {string|null} import_url
 * @property {number} coop_percentage_100
 * @property {boolean} managed_address_book
 * @property {boolean} multiple_address_shipping
 * @property {boolean|null} portal_has_top_menu
 * @property {string|null} portal_header_phone_number
 * @property {string|null} contact_email
 * @property {string|null} contact_phone_1
 * @property {string|null} contact_phone_2
 * @property {string|null} contact_address
 * @property {string|null} contact_map_lat
 * @property {string|null} contact_map_lng
 * @property {boolean} contact_form
 * @property {string|null} contact_recipient_email
 * @property {boolean} has_guest_checkout
 * @property {string|null} tax_address_line1
 * @property {string|null} tax_address_city
 * @property {string|null} tax_address_region
 * @property {string|null} tax_address_country
 * @property {string|null} tax_address_postal_code
 * @property {boolean|null} portal_has_promo
 * @property {ImageUpload} portal_promo_image
 * @property {boolean} maintenance_mode
 * @property {boolean} admin_approval_cust_acc
 * @property {boolean|null} portal_has_eshop_link
 * @property {boolean|null} portal_has_register_link
 * @property {boolean|null} portal_has_login_link
 * @property {boolean} portal_has_terms_and_conditions_link
 * @property {string|null} terms_and_conditions_link
 * @property {string} portal_code
 * @property {boolean} has_google_analytics
 * @property {string|null} legacy_google_analytics_tracking_code
 * @property {boolean} is_live
 * @property {string|null} digital_proofing_text_1
 * @property {string|null} digital_proofing_text_2
 * @property {boolean} has_sso
 * @property {boolean|null} portal_has_header_login_link
 * @property {string} custom_login_url
 * @property {boolean} login_redirect_link_selected
 * @property {string|null} login_redirect_link
 * @property {string|null} email_portal_address
 * @property {string|null} email_portal_name
 * @property {string|null} email_address_from
 * @property {ImageUpload} email_logo
 * @property {ImageUpload} packing_slip_logo
 * @property {string|null} packing_slip_portal_address
 * @property {string|null} packing_slip_support_email_address
 * @property {string|null} packing_slip_support_phone_number
 * @property {string|null} packing_slip_disclaimer
 * @property {string|null} packing_slip_company_website
 * @property {string|null} packing_slip_return_address
 * @property {string|null} email_phone_number
 * @property {boolean} hybrid_address
 * @property {boolean|null} portal_has_login_link_on_signup
 * @property {string|null} backorder_text
 * @property {boolean} portal_has_signup_link_on_login_page
 * @property {boolean} is_registration_white_list
 * @property {string|null} registration_white_list_domains
 * @property {string|null} backorder_stock_text
 * @property {number|null} default_customer_group_id
 * @property {boolean|null} portal_has_header_register_link
 * @property {boolean|null} portal_has_privatized_login_page
 * @property {boolean|null} show_logo_on_privatized_login_page
 * @property {ImageUpload} product_page_background_image
 * @property {ImageUpload} sign_in_background_image
 * @property {ImageUpload} sign_up_background_image
 * @property {boolean} live_chat_enabled
 * @property {string|null} smallchat_url
 * @property {boolean} has_custom_registration_field
 * @property {string|null} custom_registration_field_label
 * @property {boolean} display_account_credits
 * @property {boolean} display_backorder_stock_text_flag
 * @property {boolean} sso_ip_check
 * @property {boolean} show_announcement
 * @property {string|null} announcement_starts_at
 * @property {string|null} announcement_ends_at
 * @property {string|null} announcement_text
 * @property {string|null} announcement_hex_color
 * @property {string|null} announcement_hex_bg
 * @property {boolean} show_login_fields
 * @property {boolean} show_login_text
 * @property {string|null} login_text
 * @property {boolean} show_stock_count
 * @property {boolean} international_shipping
 * @property {string[]} supported_countries
 * @property {number|null} ship_from_address_id
 * @property {string|null} default_carrier
 * @property {string|null} default_delivery_confirmation
 * @property {number|null} third_party_billing_id
 * @property {boolean} show_footer_contact_us
 * @property {boolean} username_active
 * @property {boolean} use_lms_points
 * @property {string[]} credit_types
 * @property {boolean} sa_uses_ccpd
 * @property {number|null} sa_ccdp_id
 * @property {boolean} collect_ccdp_at_registration
 * @property {number|null} cc_data_point_id
 * @property {string} cc_data_point_collection_method
 * @property {string|null} google_analytics_tracking_code
 * @property {boolean} validate_cc_data_point_collection
 * @property {boolean} portal_has_confirmation_wall
 * @property {string} confirmation_wall_message
 * @property {boolean} bypass_password_reset
 * @property {boolean} is_active
 * @property {boolean} is_full_import
 * @property {boolean} is_system_managed
 * @property {boolean} force_password_reset
 * @property {number} password_reset_duration
 * @property {number|null} internal_page_contact_us_id
 * @property {boolean} is_tax_exempt
 * @property {string|null} tax_exempt_proof
 * @property {boolean} use_external_link_for_terms_and_conditions
 * @property {boolean} use_internal_link_for_terms_and_conditions
 * @property {number|null} internal_page_terms_and_conditions_id
 * @property {number} agency_id
 * @property {string|null} location_code_counter
 * @property {boolean} has_address_managed
 * @property {boolean} has_address_standard
 * @property {number|null} tax_cert_exp_year
 * @property {string} address_management_method
 * @property {number|null} address_ccdp_id
 * @property {DocumentUpload} tax_exemption_file
 * @property {string|null} tax_exemption_file_uploaded_at
 * @property {string|null} dropship_contact_phone
 * @property {string|null} dropship_contact_email
 * @property {boolean} is_newly_created
 * @property {string} checkout_mode
 *
 * // Additional computed/relationship properties
 * @property {Location} default_location
 * @property {Page[]} visible_pages
 * @property {ContextDictionary} context_dictionary
 * @property {Page[]} pages
 * @property {NoUserCustomerGroup} no_user_customer_group
 * @property {PortalIntegration[]} portal_integrations
 * @property {PortalIntegration[]} portal_integrations_social
 * @property {CcDataPoint|null} cc_data_point
 * @property {CcDataPoint[]} cc_data_points
 * @property {CustomerGroup[]} customer_groups
 * @property {PortalMenu} portal_menu
 * @property {Array} portal_tag_sets
 */

/**
 * @typedef {Object} PortalOption
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} PortalsState
 * @property {'fetchAll' | 'fetchOne' | 'create' | 'update' | 'delete' | null} task - The current task of the portals slice.
 * @property {Record<number, Portal>} data
 * @property {PortalOption[]} options - Lightweight portal options for dropdowns
 *
 * @typedef {import('../slice.types').BaseSlice & PortalsState} PortalsSlice
 */

/** @type {PortalsSlice} */
export const initialPortalsState = {
  status: 'init',
  errors: [],
  task: null,
  data: {},
  options: []
}

/** @enum {string} */
export const PORTALS_ACTIONS = {
  SET_PORTALS_STATUS: 'SET_PORTALS_STATUS',
  SET_PORTALS_ERRORS: 'SET_PORTALS_ERRORS',
  SET_PORTALS_TASK: 'SET_PORTALS_TASK',

  SET_PORTALS_DATA: 'SET_PORTALS_DATA',
  SET_PORTAL_OPTIONS: 'SET_PORTAL_OPTIONS',
  ADD_PORTAL: 'ADD_PORTAL',
  UPDATE_PORTAL: 'UPDATE_PORTAL',
  DELETE_PORTAL: 'DELETE_PORTAL',

  CLEAR_PORTALS_ERRORS: 'CLEAR_PORTALS_ERRORS',
  CLEAR_PORTALS_TASK: 'CLEAR_PORTALS_TASK',
  CLEAR_PORTALS_DATA: 'CLEAR_PORTALS_DATA'
}

export const portalsTaskPropTypes = PropTypes.oneOf(['fetchAll', 'fetchOne', 'create', 'update', 'delete', null])
export const portalPropTypes = PropTypes.object
export const portalSlicePropTypes = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  task: portalsTaskPropTypes,
  data: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string
    })
  )
})

/**
 * @typedef {{ type: PORTALS_ACTIONS, payload: PortalsSlice['status'] }} PortalsStatusAction
 * @typedef {{ type: PORTALS_ACTIONS, payload: PortalsSlice['errors'] }} PortalsErrorsAction
 * @typedef {{ type: PORTALS_ACTIONS, payload: PortalsSlice['task'] }} PortalsTaskAction
 * @typedef {{ type: PORTALS_ACTIONS, payload: Portal[] }} PortalsDataAction
 * @typedef {{ type: PORTALS_ACTIONS, payload: PortalOption[] }} PortalOptionsAction
 *
 * @typedef {{ type: PORTAL_ACTIONS, payload: Portal }} PortalsAddAction
 * @typedef {{ type: PORTAL_ACTIONS, payload: Partial<Portal> }} PortalsUpdateAction
 * @typedef {{ type: PORTAL_ACTIONS, payload: number }} PortalsDeleteAction
 * @typedef {{ type: PORTALS_ACTIONS }} PortalsClearErrorsAction
 * @typedef {{ type: PORTALS_ACTIONS }} PortalsClearTaskAction
 * @typedef {{ type: PORTALS_ACTIONS }} PortalsClearDataAction
 *
 * @typedef {PortalsStatusAction | PortalsErrorsAction | PortalsTaskAction | PortalsDataAction | PortalOptionsAction | PortalsAddAction | PortalsUpdateAction | PortalsDeleteAction | PortalsClearErrorsAction | PortalsClearTaskAction | PortalsClearDataAction} PortalsAction
 * */
